// React and Modules
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import logError from '../../../utils/errorUtil.js';

// Utils
import { useAuth } from '../../context/wms-context.js';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { camelCaseToTitleCase, mergePdfs, openDocumentInNewTab } from '../../utils/common-utils.js';
import { GuidedInterfaceContext } from './context/GuidedInterfaceContext.js';
// Other Pages
import {
    getTraceDocument,
    sendChatterMessage,
    getRoChatterMentionIds,
    updateReleaseAndOrReleaseLineStatus,
    updateReleaseInspectionStatus,
} from './utils/guided-interface-functions';
import { releaseLineStatuses } from './utils/data.js';
import Modal from '../../../components/Modal.js';

export default function Trace({
    currentReleaseLine,
    documentsToPrint,
    setDocumentsToPrint,
    setShowNotification,
    releaseName,
    returnToReleaseLines,
    releaseId,
}) {
    const { currentUser } = useAuth();
    const [loadingPrintSelectedDocuments, setLoadingPrintSelectedDocuments] = useState(false);
    const [reviewReason, setReviewReason] = useState('');
    const { partInspectionSeconds, partInspectionTimeDisplay, startTimer, stopTimer } = useContext(GuidedInterfaceContext);
    const [loadingRejection, setLoadingRejection] = useState(false);
    const [reviewDropdownOpen, setReviewDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const reviewTriggerRef = useRef(null);
    const [currentDocumentData, setCurrentDocumentData] = useState(null);
    const traceDocumentLinkFieldMap = {
        ownerCode: 'traceOwnerCodeLink',
        partRecord: 'tracePartRecordLink',
        traceTag: 'traceTagLink',
        oneClickBinder: 'traceBinderOneClickLink',
    };
    const createDefaultDocument = () => ({
        loading: true,
        loaded: false,
        error: null,
        pdf: null,
        image: null,
        printDocument: false,
        text: 'Loading Document...',
    });
    const documentsToPrintReference = {
        documentsFetched: false,
        fetchingDocuments: false,
        ownerCode: createDefaultDocument(),
        partRecord: createDefaultDocument(),
        traceTag: createDefaultDocument(),
        oneClickBinder: createDefaultDocument(),
        releaseLineId: null,
    };

    const [confirmModal, setConfirmModal] = useState(false);

    useEffect(() => {
        const currentReleaseLineDocumentData = documentsToPrint.find(
            (item) => item.releaseLineId === currentReleaseLine.releaseLineId
        );
        if (!currentReleaseLineDocumentData?.documentsFetched && !currentReleaseLineDocumentData?.fetchingDocuments) {
            const newDocument = {
                ...documentsToPrintReference,
                releaseLineId: currentReleaseLine.releaseLineId,
                fetchingDocuments: true,
            };
            const updatedDocumentsToPrint = [...documentsToPrint, newDocument];
            setDocumentsToPrint(updatedDocumentsToPrint);
            fetchDocuments();
        }
    }, []);

    useEffect(() => {
        const timer = startTimer();

        return () => stopTimer(timer);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (reviewDropdownOpen && !dropdownRef?.current?.contains(event.target)) {
                setReviewDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownRef, reviewDropdownOpen]);

    useEffect(() => {
        const currentReleaseLineDocumentData = documentsToPrint.find(
            (item) => item.releaseLineId === currentReleaseLine.releaseLineId
        );
        setCurrentDocumentData(currentReleaseLineDocumentData);
    }, [documentsToPrint]);

    const updateSingleDocumentState = (documentKey, updatedDocumentData) => {
        setDocumentsToPrint((previousDocumentsToPrint) =>
            previousDocumentsToPrint.map((documentRecord) => {
                if (documentRecord.releaseLineId !== currentReleaseLine.releaseLineId) {
                    return documentRecord;
                }

                const updatedDocumentRecord = {
                    ...documentRecord,
                    [documentKey]: {
                        ...documentRecord[documentKey],
                        ...updatedDocumentData,
                    },
                };

                const allDocumentsLoaded = Object.keys(traceDocumentLinkFieldMap).every(
                    (currentDocumentKey) => updatedDocumentRecord[currentDocumentKey]?.loaded
                );

                return {
                    ...updatedDocumentRecord,
                    documentsFetched: allDocumentsLoaded,
                    fetchingDocuments: !allDocumentsLoaded,
                };
            })
        );
    };

    async function fetchDocuments() {
        const documentRequests = Object.keys(traceDocumentLinkFieldMap).map((documentKey) => ({
            documentKey,
            documentType: documentKey,
            documentLink: currentReleaseLine?.[traceDocumentLinkFieldMap[documentKey]] || null,
        }));

        const documentFetchPromises = documentRequests.map(async ({ documentKey, documentType, documentLink }) => {
            let updatedDocumentState = {
                loading: false,
                loaded: true,
                error: null,
                pdf: null,
                image: null,
                text: 'No Document Available',
                printDocument: false,
            };

            if (documentLink) {
                try {
                    const traceDocumentData = await getTraceDocument(currentUser, {
                        documentType,
                        documentLink,
                        partName: currentReleaseLine.partName,
                    });

                    updatedDocumentState = {
                        ...updatedDocumentState,
                        ...traceDocumentData,
                    };
                } catch (error) {
                    logError(
                        error,
                        `There was an error fetching the ${documentType} document for the Trace Tab. | Link: ${documentLink}`
                    );

                    updatedDocumentState = {
                        ...updatedDocumentState,
                        error: 'Error Retrieving File',
                        text: 'Error Retrieving File',
                    };
                }
            }

            updateSingleDocumentState(documentKey, updatedDocumentState);
        });

        await Promise.allSettled(documentFetchPromises);
    }

    const togglePrintDocument = (document) => {
        const updatedDocuments = documentsToPrint.map((documents) => {
            if (documents.releaseLineId === currentReleaseLine.releaseLineId) {
                return {
                    ...documents,
                    [document]: {
                        ...documents[document],
                        printDocument: !documents[document].printDocument,
                    },
                };
            }

            return documents;
        });

        setDocumentsToPrint(updatedDocuments);
    };

    const checkForSelectedDocuments = () => {
        if (!currentDocumentData) {
            return null;
        }
        const documentPdfs = Object.keys(currentDocumentData)
            .map((documentKey) => {
                const currentDocument = currentDocumentData[documentKey];
                if (
                    documentKey === 'documentsFetched' ||
                    documentKey === 'fetchingDocuments' ||
                    documentKey === 'releaseLineId' ||
                    !currentDocument?.printDocument ||
                    !currentDocument?.pdf ||
                    currentDocument?.loading
                ) {
                    return null;
                }

                return currentDocument.pdf;
            })
            .filter(Boolean);

        if (documentPdfs.length) {
            return documentPdfs;
        } else {
            return null;
        }
    };

    const handleContinueWithoutSelection = async () => {
        setLoadingPrintSelectedDocuments(true);

        await updateReleaseStatusAndReturnToReleaseLines(false);

        setLoadingPrintSelectedDocuments(false);
    };

    const updateReleaseStatusAndReturnToReleaseLines = async (showSuccessModal = true) => {
        await updateReleaseAndOrReleaseLineStatus(
            currentUser,
            releaseId,
            currentReleaseLine.releaseLineId,
            releaseLineStatuses.IN_QC
        );

        const { statusResponse, timeResponse } = await updateReleaseInspectionStatus(
            'complete',
            'trace',
            currentReleaseLine.releaseLineName,
            releaseName,
            partInspectionSeconds,
            currentUser
        );

        if (statusResponse === 'success' && timeResponse === 'success') {
            if (showSuccessModal) {
                setShowNotification({
                    shown: true,
                    status: 'success',
                    title: 'Trace Documents Printed Successfully',
                });
            }

            returnToReleaseLines();
        } else {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Failed to Update the Status of this Release Line. Please try again.',
            });
        }
    };

    const printSelectedDocuments = async () => {
        setLoadingPrintSelectedDocuments(true);

        try {
            const documentPdfs = checkForSelectedDocuments();

            if (!documentPdfs?.length) {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'No Documents Selected',
                    message: 'Please select at least one document to print.',
                });
                return;
            }

            const response = await mergePdfs(documentPdfs);

            // Open the pdf in a new tab
            openDocumentInNewTab(response);

            await updateReleaseStatusAndReturnToReleaseLines();
        } catch (error) {
            logError(error, 'There was an error printing the selected documents.');
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Printing Documents',
                message:
                    'There was an error printing the selected documents. Please try again, and if the issue persists, contact a manager.',
            });
        } finally {
            setLoadingPrintSelectedDocuments(false);
        }
    };

    const handleReviewSubmit = async () => {
        setLoadingRejection(true);

        try {
            const chatterResponse = await sendChatterMessage(
                currentUser,
                currentReleaseLine,
                reviewReason,
                getRoChatterMentionIds(currentReleaseLine)
            );

            await updateReleaseAndOrReleaseLineStatus(
                currentUser,
                releaseId,
                currentReleaseLine.releaseLineId,
                releaseLineStatuses.PENDING_SALES
            );

            const { statusResponse, timeResponse } = await updateReleaseInspectionStatus(
                'rejected',
                'trace',
                currentReleaseLine.releaseLineName,
                releaseName,
                partInspectionSeconds,
                currentUser
            );
            if (chatterResponse.success && statusResponse === 'success' && timeResponse === 'success') {
                setShowNotification({
                    shown: true,
                    status: 'success',
                    title: 'Submission Successful',
                });
            } else {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'Failed to Submit Request',
                    message: 'There was an error submitting the request. Please try again.',
                });
            }
        } finally {
            setLoadingRejection(false);
            returnToReleaseLines();
        }
    };

    return (
        <section className='px-8 pb-20'>
            <Modal
                open={confirmModal}
                setOpen={setConfirmModal}
                onConfirm={() => handleContinueWithoutSelection()}
                title={'Are you sure you want to continue without printing?'}
                confirmText={'Accept/Approve'}
                confirmButtonStyling={
                    'inline-flex w-full justify-center rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500 sm:col-start-2'
                }
            />
            <header className='flex relative mt-10'>
                <div className='flex absolute top-0 left-0 z-10 items-center h-10'>
                    <button
                        className='flex justify-center items-center px-3 w-24 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                        onClick={returnToReleaseLines}
                    >
                        <ArrowLeftIcon className='mr-2 w-6 h-6' />
                        <span>Back</span>
                    </button>
                    {currentReleaseLine.kitNumber ? (
                        <p className='ml-6 text-xl whitespace-nowrap'>
                            Kit #: <span className='font-bold'>{currentReleaseLine.kitNumber}</span>
                        </p>
                    ) : null}
                </div>

                <div className='flex-grow text-center'>
                    <h1 className='text-4xl font-bold'>
                        Trace for{' '}
                        <a
                            className='text-blue-900 underline hover:text-blue-400'
                            href={currentReleaseLine.invLineLink}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {currentReleaseLine?.invLineName}
                        </a>
                    </h1>
                    <div className='flex gap-5 justify-center mt-2 w-full text-xl'>
                        <p>
                            Part Number: <span className='font-bold'>{currentReleaseLine.partName}</span>
                        </p>
                        <p>
                            Quantity: <span className='font-bold'>{currentReleaseLine.quantity}</span>
                        </p>
                        {currentReleaseLine.ownerCodeFolderLink ? (
                            <p>
                                Owner Code Link:{' '}
                                <a
                                    href={currentReleaseLine.ownerCodeFolderLink}
                                    className='font-bold underline text-blue-primary hover:text-blue-400'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {currentReleaseLine.ownerCodeName}
                                </a>
                            </p>
                        ) : null}
                        {currentReleaseLine.cmmIpcLink ? (
                            <p>
                                CMM+IPC Link:{' '}
                                <a
                                    href={currentReleaseLine.cmmIpcLink}
                                    className='font-bold underline text-blue-primary hover:text-blue-400'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    CMM+IPC Check
                                </a>
                            </p>
                        ) : null}
                        {currentReleaseLine.purchaseOrderLink && currentReleaseLine.purchaseOrderName ? (
                            <p>
                                Purchase Order:{' '}
                                <a
                                    href={currentReleaseLine.purchaseOrderLink}
                                    className='font-bold underline text-blue-primary hover:text-blue-400'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {currentReleaseLine.purchaseOrderName}
                                </a>
                            </p>
                        ) : null}
                        {currentReleaseLine.isRepairOrderRelease && currentReleaseLine.repairOrderLink ? (
                            <p>
                                Repair Order:{' '}
                                <a
                                    href={currentReleaseLine.repairOrderLink}
                                    className='font-bold underline text-blue-primary hover:text-blue-400'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {currentReleaseLine.repairOrderName}
                                </a>
                            </p>
                        ) : null}
                    </div>
                    <p className='mt-2 text-lg font-bold'>Select Documents to Print</p>
                </div>
            </header>
            <article className='mx-16 mb-20'>
                {currentReleaseLine?.releaseHeaderInternalComments && (
                    <div className='flex justify-center mb-4 w-full'>
                        <p className='w-1/2 text-center'>
                            <span className='font-bold'>Internal Comments:</span>{' '}
                            {currentReleaseLine.releaseHeaderInternalComments}
                        </p>
                    </div>
                )}
                <div className='grid grid-cols-4 gap-4 min-h-[36vh]'>
                    {currentDocumentData &&
                        Object.keys(currentDocumentData)
                            ?.map((documentKey) => {
                                let currentDocument = currentDocumentData[documentKey];
                                if (
                                    documentKey === 'documentsFetched' ||
                                    documentKey === 'fetchingDocuments' ||
                                    documentKey === 'releaseLineId'
                                ) {
                                    return null;
                                }

                                const titleCaseDocument = camelCaseToTitleCase(documentKey);
                                if (currentDocument?.loading) {
                                    return (
                                        <div
                                            className='overflow-hidden px-2 pt-1 text-xl font-semibold rounded-lg border'
                                            key={documentKey}
                                        >
                                            <div className='flex justify-between'>{titleCaseDocument}</div>
                                            <div className='flex justify-center items-center w-full h-full min-h-48'>
                                                <LoadingSpinner className='w-16' spinnerSize={'10'} />
                                            </div>
                                        </div>
                                    );
                                }

                                if (!currentDocument?.pdf) {
                                    return (
                                        <div
                                            className='overflow-hidden px-2 pt-1 text-xl font-semibold rounded-lg border'
                                            key={documentKey}
                                        >
                                            <div className='flex justify-between'>{titleCaseDocument}</div>
                                            <div className='flex justify-center items-center w-full h-full'>
                                                {currentDocument?.text}
                                            </div>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div
                                            key={documentKey}
                                            className={`cursor-pointer font-semibold text-xl pt-1 px-2 border rounded-lg overflow-hidden
                                        ${currentDocument?.printDocument && 'bg-blue-200 border-blue-400'}`}
                                            onClick={() => togglePrintDocument(documentKey)}
                                        >
                                            <div className='flex justify-between'>
                                                {titleCaseDocument}
                                                <CheckCircleIcon
                                                    className={`h-8 w-8 rounded-full ${
                                                        currentDocument?.printDocument ? 'text-green-400' : 'text-gray-400'
                                                    }`}
                                                />
                                            </div>
                                            <div className='flex justify-center items-center pt-3 w-auto h-full'>
                                                <img
                                                    src={`data:image/jpeg;base64,${currentDocument?.image}`}
                                                    alt='Owner Code'
                                                    className='object-contain mb-6 max-w-full max-h-full bg-white rounded-md border border-black'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDocumentInNewTab(currentDocument?.pdf);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                }
                            })
                            .filter(Boolean)}
                </div>
                <div className='flex justify-center'>
                    <button
                        disabled={loadingPrintSelectedDocuments}
                        className='px-3 py-2 mx-5 mt-8 w-full text-2xl font-semibold text-white bg-green-500 rounded-md border'
                        onClick={() => {
                            if (checkForSelectedDocuments()) {
                                printSelectedDocuments();
                            } else {
                                setConfirmModal(true);
                            }
                        }}
                    >
                        {loadingPrintSelectedDocuments ? (
                            <div className='flex justify-center items-center'>
                                <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                            </div>
                        ) : checkForSelectedDocuments() ? (
                            'Print Selected Documents'
                        ) : (
                            'Continue Without Printing'
                        )}
                    </button>
                </div>
                <div className='mt-10 bg-gray-100 rounded-lg'>
                    <p className='pt-8 pl-3 text-2xl font-bold'>Request Review</p>
                    <div className='p-6'>
                        <div ref={dropdownRef} className='relative mb-3'>
                            <button
                                ref={reviewTriggerRef}
                                type='button'
                                className='inline-flex justify-center px-4 py-2 bg-white rounded border border-gray-700 filter-button focus:outline-none focus:ring focus:ring-blue-200'
                                onClick={() => setReviewDropdownOpen(!reviewDropdownOpen)}
                            >
                                Update Request Text
                                {reviewDropdownOpen ? (
                                    <ChevronUpIcon className='ml-2 w-5 h-5' />
                                ) : (
                                    <ChevronDownIcon className='ml-2 w-5 h-5' />
                                )}
                            </button>
                            <RejectReviewDropdown
                                reviewDropdownOpen={reviewDropdownOpen}
                                triggerRef={reviewTriggerRef}
                                setReviewReason={setReviewReason}
                                setReviewDropdownOpen={setReviewDropdownOpen}
                                releaseLineName={currentReleaseLine.releaseLineName}
                            />
                        </div>

                        <button
                            disabled={reviewReason?.length < 1 || loadingRejection}
                            className='py-2 w-full text-2xl font-semibold text-white bg-red-600 rounded-md border cursor-pointer'
                            onClick={handleReviewSubmit}
                        >
                            {loadingRejection ? (
                                <div className='flex justify-center items-center'>
                                    <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                                </div>
                            ) : (
                                'Submit Request'
                            )}
                        </button>
                    </div>
                </div>
            </article>
        </section>
    );
}

function RejectReviewDropdown({ reviewDropdownOpen, triggerRef, setReviewDropdownOpen, setReviewReason, releaseLineName }) {
    const menuRef = useRef(null);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownOptions = [
        { value: 'Insufficient Qty on documentation', checked: false },
        { value: 'Trace not readable', checked: false },
        { value: 'No 8130 tag', checked: false },
        { value: 'Needs to be added to binder', checked: false },
        { value: 'Wrong condition code on documentation', checked: false },
        { value: 'Other', checked: false },
    ];
    const [checkedReasons, setCheckedReasons] = useState(dropdownOptions);
    const [userInput, setUserInput] = useState('');

    const handleCheckedUpdate = (dropdownOption, isChecked) => {
        let updatedReasons;

        if (dropdownOption.value === 'Other' && !dropdownOption.checked) {
            setReviewDropdownOpen(false);
        }

        updatedReasons = checkedReasons.map((checkedReason) => {
            if (checkedReason.value === dropdownOption.value) {
                checkedReason.checked = isChecked;
            }

            return checkedReason;
        });

        setCheckedReasons(updatedReasons);
    };

    useEffect(() => {
        let reviewRequestMessage = '';

        if (checkedReasons.some((req) => req.checked)) {
            reviewRequestMessage = `Release Line ${releaseLineName} was rejected due to the following reason(s): \n`;
            checkedReasons.forEach((req) => {
                if (req.checked && req.value === 'Other') {
                    reviewRequestMessage += `- ${userInput}`;
                }
                if (req.checked && req.value !== 'Other') {
                    reviewRequestMessage += `- ${req.value} \n`;
                }
            });
        }

        setReviewReason(reviewRequestMessage);
    }, [checkedReasons, userInput, releaseLineName, setReviewReason]);

    // This is a handler for the drop down so that it can drop "up" when there's not enough screen
    useLayoutEffect(() => {
        if (!reviewDropdownOpen) return;

        const updatePlacement = () => {
            const trigger = triggerRef.current;
            const menu = menuRef.current;
            if (!trigger || !menu) return;

            const triggerRect = trigger.getBoundingClientRect();
            const menuHeight = menu.offsetHeight;
            const viewportPadding = 8;
            const spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
            const spaceAbove = triggerRect.top - viewportPadding;

            setOpenUpward(spaceBelow < menuHeight && spaceAbove >= menuHeight);
        };

        updatePlacement();
        window.addEventListener('resize', updatePlacement);
        window.addEventListener('scroll', updatePlacement, true);

        return () => {
            window.removeEventListener('resize', updatePlacement);
            window.removeEventListener('scroll', updatePlacement, true);
        };
    }, [reviewDropdownOpen, triggerRef]);

    return (
        <section className='flex flex-col'>
            {reviewDropdownOpen ? (
                <div
                    ref={menuRef}
                    className={`absolute left-0 z-30 flex flex-col gap-y-1 p-2 bg-white rounded border border-gray-300 shadow-lg w-max min-w-full ${
                        openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}
                >
                    {checkedReasons.map((dropdownOption, index) => (
                        <label key={index} className='flex items-center w-full cursor-pointer'>
                            <input
                                type='checkbox'
                                className='w-4 h-4 form-checkbox'
                                checked={checkedReasons.find(
                                    (reason) => reason.value === dropdownOption.value && reason.checked
                                )}
                                onChange={(e) => handleCheckedUpdate(dropdownOption, e.target.checked)}
                            />
                            <span className='ml-2 text-sm'>{dropdownOption.value}</span>
                        </label>
                    ))}
                </div>
            ) : null}
            {checkedReasons.some((req) => req.checked && req.value === 'Other') ? (
                <input
                    className='px-3 py-2 mt-2 rounded-md border border-black'
                    placeholder='Enter custom review message here'
                    onChange={(e) => setUserInput(e.target.value)}
                />
            ) : null}
        </section>
    );
}
